import { MessageChannel } from "@redotech/crdb-enums/shopper-ai/message-channel";
import { ConversationPlatform } from "@redotech/redo-support-conversation-protocol-common/conversation-protocol";
import { stringHash } from "@redotech/util/string";
import * as z from "zod";

/** Read-time fallback for teams that never saved a persona; kept fixed so an unset agent never changes names. */
export const DEFAULT_SHOPPER_AGENT_PERSONA_NAME = "Jessica";

export const SHOPPER_AGENT_PERSONA_NAME_POOL: readonly string[] = [
  "Jessica",
  "Emily",
  "Sarah",
  "Rachel",
  "Lauren",
  "Megan",
  "Hannah",
  "Katie",
  "Amanda",
  "Ashley",
  "Nicole",
  "Rebecca",
  "Allison",
  "Melissa",
  "Natalie",
  "Olivia",
  "Grace",
  "Chloe",
  "Sophie",
  "Abby",
  "Emma",
  "Claire",
  "Lily",
  "Maya",
  "Kelly",
  "Erin",
  "Molly",
  "Jenna",
  "Amy",
  "Laura",
  "Anna",
  "Julia",
  "Leah",
  "Kayla",
  "Paige",
  "Danielle",
  "Michelle",
  "Andrea",
  "Vanessa",
  "Gabby",
  "Sofia",
  "Hailey",
  "Mia",
  "Elena",
  "Lucy",
  "Brooke",
  "Kate",
  "Jenny",
  "Carly",
  "Tessa",
  "Jake",
  "Ryan",
  "Tyler",
  "Matt",
  "Josh",
  "Chris",
  "Mike",
  "Daniel",
  "Andrew",
  "Ben",
  "Nick",
  "Kevin",
  "Brian",
  "Jason",
  "Eric",
  "Adam",
  "Sam",
  "Luke",
  "Nathan",
  "Ethan",
  "Dylan",
  "Kyle",
  "Sean",
  "Justin",
  "Brandon",
  "Austin",
  "Evan",
  "Aaron",
  "Zach",
  "Logan",
  "Noah",
  "Caleb",
  "Owen",
  "Jack",
  "Will",
  "Drew",
  "Cody",
  "Marcus",
  "Carlos",
  "Diego",
  "Mateo",
  "Jared",
  "Trevor",
  "Derek",
  "Scott",
  "Greg",
  "Joe",
  "Danny",
  "Tony",
  "Isaac",
  "Taylor",
  "Jordan",
  "Alex",
  "Morgan",
  "Casey",
  "Riley",
  "Jamie",
  "Avery",
  "Quinn",
  "Cameron",
];

/** Starting persona name for a team, seeded by its id so every surface offers the same one. */
export function shopperAgentPersonaNameForTeam(teamId: string): string {
  const size = SHOPPER_AGENT_PERSONA_NAME_POOL.length;
  const index = ((stringHash(teamId) % size) + size) % size;
  return (
    SHOPPER_AGENT_PERSONA_NAME_POOL[index] ?? DEFAULT_SHOPPER_AGENT_PERSONA_NAME
  );
}

export const ShopperAgentPersonaConfigSchema = z.object({
  name: z.string().optional(),
});

export type ShopperAgentPersonaConfig = z.infer<
  typeof ShopperAgentPersonaConfigSchema
>;

/**
 * Resolves the persona name the agent should use when speaking to a customer.
 * Falls back to {@link DEFAULT_SHOPPER_AGENT_PERSONA_NAME} when the team has
 * no persona configured or has cleared the name. All read sites should go
 * through this helper so the default is enforced in one place.
 */
export function getPersonaName(
  config: { name?: string | null } | null | undefined,
): string {
  return config?.name?.trim() || DEFAULT_SHOPPER_AGENT_PERSONA_NAME;
}

/**
 * History reaches the agent as `message_channel` (CRDB) or as
 * `ConversationPlatform` (Mongo support conversations). Both spell the same
 * channels with the same string values, so one set answers for either.
 */
export type PersonaIntroChannel = MessageChannel | ConversationPlatform;

/**
 * Channels the agent sends outreach on, and so the only ones a prior intro
 * could have come from. A channel qualifies only if it is two-way text *and*
 * is us — Attentive and Postscript are text but third-party senders on their
 * own numbers, while `mobileMessage` is our own iMessage pipeline.
 *
 * This decides only whether an intro already happened. Whether the agent may
 * introduce itself is `isTextConcierge` in the vanguard-loop runner; adding a
 * channel here means changing that too.
 */
const PERSONA_INTRO_CHANNELS: ReadonlySet<PersonaIntroChannel> = new Set([
  MessageChannel.Sms,
  MessageChannel.Mobilemessage,
]);

export function canChannelCarryPersonaIntro(
  channel: PersonaIntroChannel | null | undefined,
): boolean {
  return !!channel && PERSONA_INTRO_CHANNELS.has(channel);
}
